import React, { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Plus, Edit, Trash2, Tag, Loader2, GripVertical, BookOpen } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import AdminLayout from './AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBlogCategories = () => {
  const { toast } = useToast();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', color: '#6366f1', order: 0 });

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/admin/content/categories?type=blog`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setCategories(await res.json());
    } catch {
      toast({ title: 'Error', description: 'Failed to load', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCategories(); }, []);

  const openDialog = (cat = null) => {
    setEditing(cat);
    setForm(cat
      ? { name: cat.name, description: cat.description || '', color: cat.color || '#6366f1', order: cat.order || 0 }
      : { name: '', description: '', color: '#6366f1', order: categories.length }
    );
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast({ title: 'Name required', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const url = editing
        ? `${API_URL}/api/admin/content/categories/${editing.id}`
        : `${API_URL}/api/admin/content/categories`;
      const res = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, type: 'blog' }),
      });
      if (res.ok) {
        toast({ title: 'Saved', description: `Category ${editing ? 'updated' : 'created'}` });
        setDialogOpen(false);
        fetchCategories();
      } else {
        const err = await res.json();
        toast({ title: 'Error', description: err.detail || 'Failed', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error', description: 'Network error', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`Delete "${cat.name}"?`)) return;
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${API_URL}/api/admin/content/categories/${cat.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: 'Deleted' });
      fetchCategories();
    } catch {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return iso; }
  };

  return (
    <AdminLayout>
      <div className="space-y-6 w-full">

        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Blog Categories</h1>
            <p className="text-gray-500 text-sm mt-0.5">
              Organize your blog posts into categories for easy browsing.
            </p>
          </div>
          <Button className="bg-violet-600 hover:bg-violet-700 gap-1.5 flex-shrink-0" onClick={() => openDialog()}>
            <Plus className="w-4 h-4" />
            Add Category
          </Button>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Total Categories', value: categories.length },
            { label: 'Active', value: categories.filter(c => c.is_active !== false).length },
            { label: 'Total Blog Posts', value: categories.reduce((acc, c) => acc + (c.post_count ?? 0), 0) },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl border border-gray-100 px-5 py-4">
              <p className="text-2xl font-bold text-gray-900 tabular-nums">{s.value}</p>
              <p className="text-sm text-gray-500 font-medium mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
          </div>
        ) : categories.length === 0 ? (
          /* Empty state — full-width table shell */
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 tracking-wide w-8" />
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 tracking-wide">Category</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 tracking-wide">Description</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 tracking-wide">Posts</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 tracking-wide">Status</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 tracking-wide">Created</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={7} className="py-16 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center">
                        <Tag className="w-6 h-6 text-emerald-500" />
                      </div>
                      <p className="font-semibold text-gray-900">No blog categories yet</p>
                      <p className="text-sm text-gray-500">Create categories to organize your blog posts.</p>
                      <Button className="bg-violet-600 hover:bg-violet-700 gap-1.5 mt-1" onClick={() => openDialog()}>
                        <Plus className="w-4 h-4" /> Create first category
                      </Button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 tracking-wide w-8" />
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 tracking-wide">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 tracking-wide">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 tracking-wide">Posts</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 tracking-wide">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 tracking-wide">Created</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {categories.map(cat => (
                  <tr key={cat.id} className="hover:bg-gray-50/50 transition-colors group">
                    <td className="px-4 py-3.5">
                      <GripVertical className="w-4 h-4 text-gray-300 cursor-grab" />
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: cat.color || '#6366f1' }}
                        />
                        <span className="font-medium text-gray-900">{cat.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-gray-500 max-w-xs">
                      <span className="truncate block">{cat.description || '—'}</span>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-1.5 text-gray-700">
                        <BookOpen className="w-3.5 h-3.5 text-gray-400" />
                        <span className="tabular-nums font-medium">{cat.post_count ?? 0}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <Badge className={`text-[11px] border-0 ${
                        cat.is_active === false
                          ? 'bg-gray-100 text-gray-500'
                          : 'bg-emerald-100 text-emerald-700'
                      }`}>
                        {cat.is_active === false ? 'Inactive' : 'Active'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3.5 text-gray-500 text-xs tabular-nums">
                      {formatDate(cat.created_at)}
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-1 justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-gray-400 hover:text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => openDialog(cat)}
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleDelete(cat)}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Category' : 'New Blog Category'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Tutorials"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Optional"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Color</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={form.color}
                    onChange={e => setForm({ ...form, color: e.target.value })}
                    className="w-10 h-10 rounded border cursor-pointer"
                  />
                  <Input
                    value={form.color}
                    onChange={e => setForm({ ...form, color: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Order</Label>
                <Input
                  type="number"
                  value={form.order}
                  onChange={e => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
                  min={0}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-violet-600 hover:bg-violet-700">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminBlogCategories;
